import type { MapDocument, TilesetRef } from "../document/types.ts";
import { LAYER_ITEMS_MAX } from "../live/resolve-types.ts";
import { hash } from "../random.ts";
import { scatterDab, type DabResult, type DabStop, type ScatterPlacement } from "./scatter.ts";
import type { ScatterKit } from "./scatter-entries.ts";
import type { ScatterOccupancy } from "./scatter-occupancy.ts";
import { addDab, newTally, type WhyTally } from "./scatter-why.ts";
import type { PropRef } from "./templates.ts";

/**
 * The prop brush's stroke (ADR-123 decisions 2.6 and 2.7): its seed from the map's seed and the tab's stroke
 * counter, a dab's seed from the stroke's and the dab's centre, so the ghosts at a cell are exactly the first dab a
 * click there places; dabs stepped along the pointer's path at the spacing; and a stop at `STROKE_PROPS_MAX` props or
 * the active layer's room under `LAYER_ITEMS_MAX`, whichever comes first.
 */

/**
 * The most props one stroke places.
 */
export const STROKE_PROPS_MAX = 2000;

/**
 * The most known ground cover one stroke clears (ADR-123's amendment of 2026-10-13, A3.7), as many as it places.
 */
export const COVER_CLEAR_MAX = 2000;

/**
 * A stroke in progress: its seed, the last dab's centre in cells (null before the first), the props it may still
 * place, the cover it may still clear, why it stopped (null while it runs), its placements in the order made and the
 * cover they cleared in the order cleared, and the tally of what its dabs tried and refused.
 */
export interface ScatterStroke {
  readonly seed: number;
  last: { readonly x: number; readonly y: number } | null;
  room: number;
  clearRoom: number;
  stopped: DabStop | null;
  readonly placements: ScatterPlacement[];
  readonly cleared: PropRef[];
  readonly tally: WhyTally;
}

/**
 * What every dab of a stroke shares: the prepared set, the map, the world's tilesets and its id, the radius in tiles
 * and the density.
 */
export interface StrokeInput {
  readonly kit: ScatterKit;
  readonly map: MapDocument;
  readonly tilesets: readonly TilesetRef[];
  readonly world: string;
  readonly radius: number;
  readonly density: number;
}

/**
 * A stroke's seed.
 *
 * @param mapSeed The map's seed.
 * @param counter The tab's stroke counter, from 1, advanced after each stroke and on Reroll.
 * @returns `hash(mapSeed, counter)`.
 */
export function strokeSeed(mapSeed: number, counter: number): number {
  return hash(mapSeed, counter);
}

/**
 * A dab's seed.
 *
 * @param seed The stroke's seed.
 * @param centreX The dab's centre column, in cells.
 * @param centreY The dab's centre row, in cells.
 * @returns `hash(seed, centreX, centreY)`.
 */
export function dabSeed(seed: number, centreX: number, centreY: number): number {
  return hash(seed, centreX, centreY);
}

/**
 * How far the pointer travels between dabs: a tile or half the radius, whichever is more.
 *
 * @param radius The radius in tiles.
 * @param cellsPerTile Authoring cells per tile.
 * @returns The spacing in cells.
 */
export function dabSpacing(radius: number, cellsPerTile: number): number {
  return Math.max(cellsPerTile, (radius * cellsPerTile) / 2);
}

/**
 * The cell corner nearest a pointer, on the map: a dab's centre.
 *
 * @param x The pointer's column, in cells.
 * @param y The pointer's row, in cells.
 * @param map The map.
 * @param cellsPerTile Authoring cells per tile.
 * @returns The centre in cells.
 */
export function clampedCentre(x: number, y: number, map: MapDocument, cellsPerTile: number): { x: number; y: number } {
  return { x: Math.min(map.width * cellsPerTile, Math.max(0, Math.round(x))), y: Math.min(map.height * cellsPerTile, Math.max(0, Math.round(y))) };
}

/**
 * The dabs' centres a move of the pointer gives: one at each whole spacing along the segment from the last dab's
 * centre to the pointer, each rounded to the nearest cell corner, so a fast drag leaves no gap and a move shorter
 * than the spacing gives none.
 *
 * @param last The last dab's centre, in cells.
 * @param pointer The pointer's centre, in cells.
 * @param spacing The spacing in cells (`dabSpacing`).
 * @returns The centres in order.
 */
export function dabCentresTo(last: { readonly x: number; readonly y: number }, pointer: { readonly x: number; readonly y: number }, spacing: number): { x: number; y: number }[] {
  const dx = pointer.x - last.x;
  const dy = pointer.y - last.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const steps = Math.floor(length / spacing);
  const centres: { x: number; y: number }[] = [];
  for (let k = 1; k <= steps; k += 1) {
    const share = (k * spacing) / length;
    centres.push({ x: Math.round(last.x + dx * share), y: Math.round(last.y + dy * share) });
  }
  return centres;
}

/**
 * Starts a stroke.
 *
 * @param mapSeed The map's seed.
 * @param counter The tab's stroke counter.
 * @param layerProps How many items the active layer holds.
 * @returns The stroke, with room for `STROKE_PROPS_MAX` props or the layer's room under `LAYER_ITEMS_MAX`, the less.
 */
export function startStroke(mapSeed: number, counter: number, layerProps: number): ScatterStroke {
  return { seed: strokeSeed(mapSeed, counter), last: null, room: Math.max(0, Math.min(STROKE_PROPS_MAX, LAYER_ITEMS_MAX - layerProps)), clearRoom: COVER_CLEAR_MAX, stopped: null, placements: [], cleared: [], tally: newTally() };
}

/**
 * Moves a stroke's pointer: the first call places a dab at the pointer's cell, later calls a dab at each spacing
 * along the way from the last dab's centre. Each dab sees every earlier placement through the occupancy. At the
 * room's end the stroke stops, keeps what it has and places nothing more.
 *
 * @param stroke The stroke, changed in place.
 * @param pointer The pointer, in cells.
 * @param input What every dab shares.
 * @param occupancy The stroke's occupancy, changed in place.
 * @returns The dabs placed by this move, in order.
 */
export function strokeTo(stroke: ScatterStroke, pointer: { readonly x: number; readonly y: number }, input: StrokeInput, occupancy: ScatterOccupancy): DabResult[] {
  if (stroke.stopped !== null) {
    return [];
  }
  const cellsPerTile = input.kit.cellsPerTile;
  const at = clampedCentre(pointer.x, pointer.y, input.map, cellsPerTile);
  const centres = stroke.last === null ? [at] : dabCentresTo(stroke.last, at, dabSpacing(input.radius, cellsPerTile));
  const dabs: DabResult[] = [];
  for (const centre of centres) {
    const result = scatterDab({ ...input, centreX: centre.x, centreY: centre.y, seed: dabSeed(stroke.seed, centre.x, centre.y), room: stroke.room, clearRoom: stroke.clearRoom }, occupancy);
    stroke.last = centre;
    for (const ref of result.cleared) {
      stroke.cleared.push(ref);
    }
    stroke.clearRoom -= result.cleared.length;
    for (const placement of result.placements) {
      stroke.placements.push(placement);
      stroke.room -= (input.kit.entries[placement.entry]?.pieces.length ?? 1);
    }
    addDab(stroke.tally, result);
    dabs.push(result);
    if (result.stop === "cap") {
      stroke.stopped = "cap";
      break;
    }
  }
  return dabs;
}
